<template>
  <div class="tsn-intro" ref="section">
    <div class="tsn-animation" :class="{ 'animation-triggered': gifVisible }">
      <div class="frame-container">
        <img
          v-if="gifVisible && currentImage"
          :src="currentImage"
          alt="Intro Animation"
          key="sequence-frame"
          class="frame-img"
        />
      </div>
    </div>
    <h1 class="tsn-title">
      <span class="gradient animate-line delay-2">
        <div v-html="$t('TSKSyringe.TSN1')"></div>
      </span>
      <span class="line-1 animate-line delay-1">
        <div v-html="$t('TSKSyringe.TSN2')"></div>
      </span>
    </h1>
  </div>
</template>

<script>
export default {
  data() {
    return {
      gifVisible: false,
      hasPlayed: false,
      isMobile: false,
      currentFrame: 1,
      frameLoaded: {},
      autoplayTimer: null,
      fps: 24,
      portraitConfig: {
        base: '/mp4/TSY/TSN/TSN_phone/frame_',
        frames: 100,
        digits: 3
      },
      landscapeConfig: {
        base: '/mp4/TSY/TSN/TSN/frame_',
        frames: 144,
        digits: 3
      }
    }
  },
  computed: {
    config() {
      return this.isMobile ? this.portraitConfig : this.landscapeConfig
    },
    currentImage() {
      const cur = this.currentFrame
      return this.frameLoaded[cur] ? this.frameLoaded[cur].src : null
    }
  },
  mounted() {
    this.checkDevice()
    window.addEventListener("resize", this.handleResize)
    this.preloadAllFrames()

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.intersectionRatio >= 0.66 && !this.hasPlayed) {
          this.hasPlayed = true
          this.startAutoplay()
          observer.disconnect()
        }
      },
      { threshold: [0, 0.33, 0.66, 1] }
    )
    observer.observe(this.$refs.section)
  },
  beforeUnmount() {
    window.removeEventListener("resize", this.handleResize)
    this.stopAutoplay()
  },
  methods: {
    handleResize() {
      const wasMobile = this.isMobile
      this.checkDevice()
      if (this.isMobile !== wasMobile) {
        this.stopAutoplay()
        this.frameLoaded = {}
        this.currentFrame = 1
        this.gifVisible = false
        this.preloadAllFrames()
      }
    },
    checkDevice() {
      this.isMobile = window.innerWidth <= 768
    },
    preloadAllFrames() {
      let loadedCount = 0
      const total = this.config.frames
      for (let i = 1; i <= total; i++) {
        const n = String(i).padStart(this.config.digits, '0')
        const img = new Image()
        img.src = `${this.config.base}${n}.jpg`
        img.onload = () => {
          this.$set 
            ? this.$set(this.frameLoaded, i, img) 
            : (this.frameLoaded[i] = img)
          loadedCount++
          if (loadedCount === total) {
            this.gifVisible = true
            if (this.hasPlayed) this.startAutoplay()
          }
        }
      }
    },
    startAutoplay() {
      this.stopAutoplay()
      this.autoplayTimer = setInterval(() => {
        if (this.currentFrame < this.config.frames) {
          this.currentFrame++
        } else {
          this.currentFrame = 1
        }
      }, 1000 / this.fps)
    },
    stopAutoplay() {
      if (this.autoplayTimer) {
        clearInterval(this.autoplayTimer)
        this.autoplayTimer = null
      }
    }
  }
}
</script>

<style scoped>
@keyframes fadeUp {
  0% {
    opacity: 0;
    transform: translateY(30px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
}

.tsn-intro {
  height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  background: transparent;
  overflow: hidden;
  flex-direction: column;
}

.tsn-animation {
  opacity: 0;
  transition: opacity 0.8s ease;
}
.tsn-animation.animation-triggered {
  opacity: 1;
}

.tsn-animation img {
  width: 100%;
  max-height: 100vh;
  object-fit: contain;
  display: block;
}

.tsn-title {
  position: absolute;
  top: 25%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 90vw;
  font-family: 'PingFang TC', 'Open Sans', 'Noto Sans TC', 'Heiti TC', sans-serif;
  font-size: clamp(1rem, 2vw, 2rem);
  text-align: center;
  line-height: 1.5;
  z-index: 10;
  max-width: 90vw;
  letter-spacing: 0.05em;
  padding: 0 1rem;
}

.line-1 {
  display: block;
  color: #111;
  font-weight: 400;
}

.gradient {
  background: linear-gradient(to bottom, #AC7FD6, #68C6C2);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  display: inline-block;
  font-weight: 600;
  letter-spacing: 0.16em;
}

.animate-line {
  opacity: 0;
  animation: fadeUp 0.8s ease-out forwards;
}
.delay-1 {
  animation-delay: 0.2s;
}
.delay-2 {
  animation-delay: 0.6s;
}
</style>
